import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TShopifyCreateSalesChannelRequest,
	TShopifySalesChannel,
	TShopifySalesChannelsCatalogRequest,
	TShopifySalesChannelsCatalogResponse,
	TShopifyStoreSalesChannelsRequest,
	TShopifyStoreSalesChannelsResponse,
	TShopifyStoreSettingsRequest,
	TShopifyStoreSettingsResponse,
	TShopifyStoreSyncRequest,
	TShopifyStoreSyncResponse,
	TShopifyStoreUpdateSalesChannelsRequest,
	TShopifyStoreUpdateSettingsRequest,
	TShopifyStoreUpdateSettingsResponse,
	TShopifyStoresByClientIdRequest,
	TShopifyStoresByClientIdResponse,
	TShopifyStoresStore,
	TShopifyUpdateSalesChannelRequest,
	TShopifyDeleteSalesChannelRequest,
} from './types';
import { SHOPIFY_STORES_SLICE_NAME } from './constants';

const initialState: TShopifyStoresStore = {
	storesByClientId: null,
	storesByClientIdLoading: false,

	storeSettings: null,
	storeSettingsLoading: false,

	updateStoreSettings: null,
	updateStoreSettingsLoading: false,
	updateStoreSettingsSuccess: false,

	storeSalesChannels: null,
	storeSalesChannelsLoading: false,

	updateStoreSalesChannels: null,
	updateStoreSalesChannelsLoading: false,
	updateStoreSalesChannelsSuccess: false,

	syncStoreResult: null,
	syncStoreLoading: false,
	syncStoreSuccess: false,

	salesChannelsCatalog: null,
	salesChannelsCatalogLoading: false,

	salesChannel: null,
	salesChannelLoading: false,

	createSalesChannelLoading: false,
	createSalesChannelSuccess: false,

	updateSalesChannelLoading: false,
	updateSalesChannelSuccess: false,

	deleteSalesChannelLoading: false,
	deleteSalesChannelSuccess: false,
};

const shopifyStoresSlice = createSlice({
	name: SHOPIFY_STORES_SLICE_NAME,
	initialState,
	reducers: {
		getStoresByClientIdRequest: (
			state,
			_action: PayloadAction<TShopifyStoresByClientIdRequest>,
		) => {
			state.storesByClientIdLoading = true;
		},
		getStoresByClientIdSuccess: (
			state,
			action: PayloadAction<TShopifyStoresByClientIdResponse>,
		) => {
			state.storesByClientId = action.payload;
			state.storesByClientIdLoading = false;
		},
		getStoresByClientIdFailure: (state) => {
			state.storesByClientIdLoading = false;
		},
		clearStoresByClientId: (state) => {
			state.storesByClientId = null;
		},

		getStoreSettingsRequest: (state, _action: PayloadAction<TShopifyStoreSettingsRequest>) => {
			state.storeSettingsLoading = true;
		},
		getStoreSettingsSuccess: (state, action: PayloadAction<TShopifyStoreSettingsResponse>) => {
			state.storeSettings = action.payload;
			state.storeSettingsLoading = false;
		},
		getStoreSettingsFailure: (state) => {
			state.storeSettingsLoading = false;
		},
		clearStoreSettings: (state) => {
			state.storeSettings = null;
		},

		updateStoreSettingsRequest: (
			state,
			_action: PayloadAction<TShopifyStoreUpdateSettingsRequest>,
		) => {
			state.updateStoreSettingsLoading = true;
			state.updateStoreSettingsSuccess = false;
		},
		updateStoreSettingsSuccess: (
			state,
			action: PayloadAction<TShopifyStoreUpdateSettingsResponse>,
		) => {
			state.updateStoreSettings = action.payload;
			state.updateStoreSettingsLoading = false;
			state.updateStoreSettingsSuccess = true;
		},
		updateStoreSettingsFailure: (state) => {
			state.updateStoreSettingsLoading = false;
		},
		resetUpdateStoreSettingsSuccess: (state) => {
			state.updateStoreSettingsSuccess = false;
		},

		getStoreSalesChannelsRequest: (
			state,
			_action: PayloadAction<TShopifyStoreSalesChannelsRequest>,
		) => {
			state.storeSalesChannelsLoading = true;
		},
		getStoreSalesChannelsSuccess: (
			state,
			action: PayloadAction<TShopifyStoreSalesChannelsResponse>,
		) => {
			state.storeSalesChannels = action.payload;
			state.storeSalesChannelsLoading = false;
		},
		getStoreSalesChannelsFailure: (state) => {
			state.storeSalesChannelsLoading = false;
		},
		clearStoreSalesChannels: (state) => {
			state.storeSalesChannels = null;
		},

		updateStoreSalesChannelsRequest: (
			state,
			_action: PayloadAction<TShopifyStoreUpdateSalesChannelsRequest>,
		) => {
			state.updateStoreSalesChannelsLoading = true;
			state.updateStoreSalesChannelsSuccess = false;
		},
		updateStoreSalesChannelsSuccess: (
			state,
			action: PayloadAction<TShopifyStoreSalesChannelsResponse>,
		) => {
			state.updateStoreSalesChannels = action.payload;
			state.updateStoreSalesChannelsLoading = false;
			state.updateStoreSalesChannelsSuccess = true;
		},
		updateStoreSalesChannelsFailure: (state) => {
			state.updateStoreSalesChannelsLoading = false;
		},
		resetUpdateStoreSalesChannelsSuccess: (state) => {
			state.updateStoreSalesChannelsSuccess = false;
		},

		syncStoreRequest: (state, _action: PayloadAction<TShopifyStoreSyncRequest>) => {
			state.syncStoreLoading = true;
			state.syncStoreSuccess = false;
		},
		syncStoreSuccess: (state, action: PayloadAction<TShopifyStoreSyncResponse>) => {
			state.syncStoreResult = action.payload;
			state.syncStoreLoading = false;
			state.syncStoreSuccess = true;
		},
		syncStoreFailure: (state) => {
			state.syncStoreLoading = false;
		},
		resetSyncStoreSuccess: (state) => {
			state.syncStoreSuccess = false;
		},

		listSalesChannelsCatalogRequest: (
			state,
			_action: PayloadAction<TShopifySalesChannelsCatalogRequest>,
		) => {
			state.salesChannelsCatalogLoading = true;
		},
		listSalesChannelsCatalogSuccess: (
			state,
			action: PayloadAction<TShopifySalesChannelsCatalogResponse>,
		) => {
			state.salesChannelsCatalog = action.payload;
			state.salesChannelsCatalogLoading = false;
		},
		listSalesChannelsCatalogFailure: (state) => {
			state.salesChannelsCatalogLoading = false;
		},
		clearSalesChannelsCatalog: (state) => {
			state.salesChannelsCatalog = null;
		},

		getSalesChannelRequest: (state, _action: PayloadAction<number>) => {
			state.salesChannelLoading = true;
		},
		getSalesChannelSuccess: (state, action: PayloadAction<TShopifySalesChannel>) => {
			state.salesChannel = action.payload;
			state.salesChannelLoading = false;
		},
		getSalesChannelFailure: (state) => {
			state.salesChannelLoading = false;
		},
		clearSalesChannel: (state) => {
			state.salesChannel = null;
		},

		createSalesChannelRequest: (
			state,
			_action: PayloadAction<TShopifyCreateSalesChannelRequest>,
		) => {
			state.createSalesChannelLoading = true;
			state.createSalesChannelSuccess = false;
		},
		createSalesChannelSuccess: (state) => {
			state.createSalesChannelLoading = false;
			state.createSalesChannelSuccess = true;
		},
		createSalesChannelFailure: (state) => {
			state.createSalesChannelLoading = false;
		},
		resetCreateSalesChannelSuccess: (state) => {
			state.createSalesChannelSuccess = false;
		},

		updateSalesChannelRequest: (
			state,
			_action: PayloadAction<TShopifyUpdateSalesChannelRequest>,
		) => {
			state.updateSalesChannelLoading = true;
			state.updateSalesChannelSuccess = false;
		},
		updateSalesChannelSuccess: (state) => {
			state.updateSalesChannelLoading = false;
			state.updateSalesChannelSuccess = true;
		},
		updateSalesChannelFailure: (state) => {
			state.updateSalesChannelLoading = false;
		},
		resetUpdateSalesChannelSuccess: (state) => {
			state.updateSalesChannelSuccess = false;
		},

		deleteSalesChannelRequest: (
			state,
			_action: PayloadAction<TShopifyDeleteSalesChannelRequest>,
		) => {
			state.deleteSalesChannelLoading = true;
			state.deleteSalesChannelSuccess = false;
		},
		deleteSalesChannelSuccess: (state) => {
			state.deleteSalesChannelLoading = false;
			state.deleteSalesChannelSuccess = true;
		},
		deleteSalesChannelFailure: (state) => {
			state.deleteSalesChannelLoading = false;
		},
		resetDeleteSalesChannelSuccess: (state) => {
			state.deleteSalesChannelSuccess = false;
		},
	},
});

export const {
	getStoresByClientIdRequest,
	getStoresByClientIdSuccess,
	getStoresByClientIdFailure,
	clearStoresByClientId,
	getStoreSettingsRequest,
	getStoreSettingsSuccess,
	getStoreSettingsFailure,
	clearStoreSettings,
	updateStoreSettingsRequest,
	updateStoreSettingsSuccess,
	updateStoreSettingsFailure,
	resetUpdateStoreSettingsSuccess,
	getStoreSalesChannelsRequest,
	getStoreSalesChannelsSuccess,
	getStoreSalesChannelsFailure,
	clearStoreSalesChannels,
	updateStoreSalesChannelsRequest,
	updateStoreSalesChannelsSuccess,
	updateStoreSalesChannelsFailure,
	resetUpdateStoreSalesChannelsSuccess,
	syncStoreRequest,
	syncStoreSuccess,
	syncStoreFailure,
	resetSyncStoreSuccess,
	listSalesChannelsCatalogRequest,
	listSalesChannelsCatalogSuccess,
	listSalesChannelsCatalogFailure,
	clearSalesChannelsCatalog,
	getSalesChannelRequest,
	getSalesChannelSuccess,
	getSalesChannelFailure,
	clearSalesChannel,
	createSalesChannelRequest,
	createSalesChannelSuccess,
	createSalesChannelFailure,
	resetCreateSalesChannelSuccess,
	updateSalesChannelRequest,
	updateSalesChannelSuccess,
	updateSalesChannelFailure,
	resetUpdateSalesChannelSuccess,
	deleteSalesChannelRequest,
	deleteSalesChannelSuccess,
	deleteSalesChannelFailure,
	resetDeleteSalesChannelSuccess,
} = shopifyStoresSlice.actions;

export default shopifyStoresSlice.reducer;
